import React, { useEffect } from 'react';
import { Input, InputGroup, InputGroupText } from 'reactstrap';
import classNames from 'classnames';

import { InputProps } from '../props';
import ReadOnly from './ReadOnly';

interface ComponentProps extends InputProps {
  noDecimals?: boolean;
  className?: string;
}

const Currency: React.FC<ComponentProps> = ({
  id,
  value,
  disabled = false,
  onChange,
  setFieldValue,
  noDecimals = false,
  className,
}) => {
  const readonlyCurrencyFormatter = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: noDecimals ? 0 : 2,
    maximumFractionDigits: noDecimals ? 0 : 2,
  });

  const formatIncomeInputString = (input: string) => {
    // Remove any non-numeric characters except the decimal point
    let value = input.replace(/[^\d.]/g, '');

    // Handle leading decimal point
    if (value.startsWith('.')) {
      value = `0${value}`;
    }

    // Split on decimal point
    const parts = value.split('.');

    // Restrict to 2 decimal places
    if (parts.length > 2 || (parts[1] && parts[1].length > 2)) {
      value = `${parts[0]}.${parts[1] ? parts[1].substring(0, 2) : ''}`;
    }

    return value;
  };

  const formatCurrency = (value: string): string => parseFloat(value.trim() || '0').toFixed(2);

  const formatReadOnly = (value: string) => {
    if (value) {
      return readonlyCurrencyFormatter.format(parseFloat(value));
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const formattedInput = formatIncomeInputString(value);

    setFieldValue?.(id, formattedInput);
    onChange?.(id, formattedInput);
  };

  const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { value } = event.target;
    setFieldValue?.(id, formatCurrency(value));
  };

  useEffect(() => {
    if (!disabled) setFieldValue?.(id, formatCurrency(value?.toString() ?? ''));
  }, []);

  const inputClassName = classNames([className], {
    'ms-1': className === undefined,
  });

  return (
    <>
      {!disabled ? (
        <InputGroup>
          <InputGroupText>$</InputGroupText>
          <Input
            id={id}
            name={id}
            value={value}
            disabled={disabled}
            className={inputClassName}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            data-cy={`${id}-currency`}
          />
        </InputGroup>
      ) : (
        <ReadOnly
          className={className}
          type="readonly"
          id={id}
          name={id}
          value={formatReadOnly(value?.toString() ?? '0')}
        />
      )}
    </>
  );
};

export default Currency;
